import { useTheme } from '@material-ui/core';
import { BarLayer, BarSvgProps, ResponsiveBar, svgDefaultProps } from '@nivo/bar';
import * as d3 from 'd3';
import React, { useState } from 'react';
import { adjustTextColor } from '../../../pages/AnalysisPage/Palette/adjustTextColor';
import { ColorPalette } from '../../../pages/AnalysisPage/Palette/ColorPalette';

export interface BarChartProps {
  ResponsiveBarProps: Partial<BarSvgProps<any>>;
  palette: ColorPalette;
  colors?: 'stoplight' | 'standard';
  leftLegend?: string;
  segmentLegend?: boolean;
  valueFormatter?: (value: any) => string;
  formatSuffix?: string;
  formatPrefix?: string;
  height?: number;
  animate?: boolean;
}

export const BarChart: React.FC<BarChartProps> = ({
  ResponsiveBarProps,
  colors,
  valueFormatter,
  leftLegend,
  segmentLegend,
  formatSuffix,
  formatPrefix,
  height,
  animate = true,
  palette,
}) => {
  const theme = useTheme();
  const [chart_loaded, set_chart_loaded] = useState(false);

  // Color scheme
  let colorScheme = palette.primaryScale;
  if (colors === 'stoplight') {
    colorScheme = [...palette.performanceScale];
  }
  const textColor = adjustTextColor(palette, 0.85, 1);

  // Value formatter
  const d3AxisFormatter = d3.format('~s');
  let axisFormatter =
    valueFormatter ??
    ((v) => {
      const value = (formatPrefix ?? '') + d3AxisFormatter(v) + (formatSuffix ?? '');
      return value;
    });

  const d3LabelFormatter = d3.format('.2s');
  let labelFormatter =
    valueFormatter ??
    ((v) => {
      const value = (formatPrefix ?? '') + d3LabelFormatter(v) + (formatSuffix ?? '');
      return value;
    });

  const skipAnimation = !animate || sessionStorage.getItem('skip_animation'); // Setting for skipping animations when screenshotting

  return (
    <div
      style={height ? { height } : undefined}
      className={`justify-center min-w-0 ${height > 0 ? '' : 'h-[500px]'}`}
      data-screenshot-id={chart_loaded ? 'chart-loaded' : undefined}
    >
      <ResponsiveBar
        theme={
          {
            textColor: theme.palette.text.primary,
            labels: {
              text: {
                fontSize: 14,
              },
            },
            grid: {
              line: {
                stroke: theme.palette.grey[300],
              },
            },
            axis: {
              ticks: {
                text: theme.typography.body2,
              },
            },
            legends: {
              text: theme.typography.body2,
            },
          } as any
        }
        margin={{
          top: theme.spacing(6),
          right: theme.spacing(0),
          bottom: theme.spacing(4),
          left: theme.spacing(8),
        }}
        padding={0.3}
        layout="vertical"
        colors={colorScheme}
        borderWidth={3}
        isInteractive={false}
        borderColor={'#fff'}
        enableGridY={true}
        labelTextColor={textColor}
        valueFormat={labelFormatter}
        axisLeft={{
          format: axisFormatter,
          tickSize: 0,
          tickPadding: theme.spacing(1),
          tickRotation: 0,
          legend: leftLegend,
          legendPosition: 'middle',
          legendOffset: leftLegend ? -60 : 0,
        }}
        legends={
          segmentLegend
            ? [
                {
                  dataFrom: 'keys',
                  anchor: 'top',
                  direction: 'row',
                  itemWidth: 80,
                  itemHeight: 20,
                  translateY: -50,
                  itemDirection: 'left-to-right',
                },
              ]
            : []
        }
        enableLabel={true}
        animate={skipAnimation ? false : true}
        labelFormat={labelFormatter}
        labelSkipHeight={20}
        labelSkipWidth={20}
        data={ResponsiveBarProps?.data as object[]}
        layers={
          [
            ...svgDefaultProps.layers,
            () => {
              // This is just sort of a hack to indicate that the chart has loaded, as indicated by the maker of Nivo
              // https://github.com/plouc/nivo/issues/527
              // https://codesandbox.io/s/won5y6k70w?file=/src/BarDemo.jsx:94-109
              !chart_loaded && set_chart_loaded(true);
              return <text />;
            },
          ] as BarLayer<any>[]
        }
        {...ResponsiveBarProps}
      />
    </div>
  );
};

export default BarChart;
